import { inject, injectable } from 'inversify';
import { Observable } from 'rxjs';

import { ResponsePipe } from '../utils/request.utils';
import { ApiConfig } from './api-config';
import { LightHttp } from './light-http.service';
import { LoginService } from './login.service';

/**
 * Post-Stripe-upgrade: the body shape is `{ paymentMethodId, planName }` (not the
 * legacy `{ stripeToken, planName }`) — the backend rejects legacy token bodies with
 * HTTP 409 and a "refresh the page" message (stripe-sdk-upgrade.md §4.2).
 *
 * `payStripe` may return a SCA challenge:
 *   `{ requires_action: true, clientSecret: string, subscriptionId: string }`
 *   Frontend then calls `stripe.confirmCardPayment(clientSecret)` and on success
 *   follows up with [confirmStripe] to finalize on the server.
 * Otherwise it returns `{ state: "success" }`.
 */
@injectable()
export class PaymentService {
    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(LightHttp) private http: LightHttp,
        @inject(LoginService) private loginService: LoginService,
    ) { }

    payStripe(paymentMethodId: string, planName: string): Observable<any> {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('Only logged in users can make a payment');
        }
        return this.http.post(this.apiConfig.getPaymentsPayEndpoint(), {
            paymentMethodId,
            planName
        }).pipe(
            ResponsePipe,
        );
    }

    /** Finalize a SCA-challenged subscription after `stripe.confirmCardPayment` succeeds. */
    confirmStripe(subscriptionId: string): Observable<any> {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('Only logged in users can confirm a payment');
        }
        return this.http.post(this.apiConfig.getPaymentsConfirmStripeEndpoint(), {
            subscriptionId
        }).pipe(
            ResponsePipe,
        );
    }

    payPayPal(subscriptionId: string, planName: string): Observable<any> {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('Only logged in users can make a payment');
        }
        return this.http.post(this.apiConfig.getPaymentsPayPalEndpoint(), {
            subscriptionId,
            planName
        }).pipe(
            ResponsePipe,
        );
    }
}
